import { Signal, inject } from '@angular/core';
import {
  QueryClient,
  injectMutation,
  injectQuery,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';
import { TranslateService } from '@ngx-translate/core';
import { IServerSideGetRowsRequest } from 'ag-grid-community';
import { firstValueFrom } from 'rxjs';

import { ToastService } from '@services/toast.service';
import {
  AlertNotification,
  AlertNotificationDayRange,
  AlertNotificationDays,
  AlertNotificationDetail,
} from 'src/app/core/models/alert-notification.models';
import { AlertNotificationApiService } from '../services/alert-notification-api.service';
import { UserApiService } from '../services/user-api.service';

/**
 * The alert notification stream (WAP-1860, backend SEN-5795). The grid page
 * reads through SSRM (ag-grid's block cache, not this cache); these hooks serve
 * the bell dropdown (the latest rows), the header badge (the unread count) and
 * the detail pane, and own the two writes: one row read, every row read.
 *
 * Both list keys carry `companyId`: the rows are scoped to the active company
 * on the backend, so a company switch re-keys the entries.
 */
export interface AlertNotificationsScope {
  userId: string;
  companyId: string;
}

const ALERT_NOTIFICATIONS_ROOT_KEY = ['user', 'alert_notifications'] as const;
const ALERT_NOTIFICATION_COUNT_ROOT_KEY = [
  'user',
  'alert_notification_count',
] as const;
const ALERT_NOTIFICATION_ROOT_KEY = ['alert_notification'] as const;

/** Rows the bell dropdown shows. */
export const LATEST_ALERT_NOTIFICATIONS_LIMIT = 10;
/** Cards per Load more step of the stream view. */
export const ALERT_NOTIFICATION_STREAM_PAGE_SIZE = 20;

export const latestAlertNotificationsQueryKey = (
  scope: AlertNotificationsScope,
) => [...ALERT_NOTIFICATIONS_ROOT_KEY, 'latest', scope] as const;

export const alertNotificationCountQueryKey = (
  scope: AlertNotificationsScope,
) => [...ALERT_NOTIFICATION_COUNT_ROOT_KEY, scope] as const;

export const alertNotificationDetailQueryKey = (eventId: number) =>
  [...ALERT_NOTIFICATION_ROOT_KEY, eventId] as const;

export const alertNotificationDaysQueryKey = (scope: AlertNotificationsScope) =>
  [...ALERT_NOTIFICATIONS_ROOT_KEY, 'days', scope] as const;

/** The grid endpoint's request for the first rows in its default order (newest trigger first). */
const latestRowsRequest = (
  endRow: number = LATEST_ALERT_NOTIFICATIONS_LIMIT,
): IServerSideGetRowsRequest => ({
  startRow: 0,
  endRow,
  rowGroupCols: [],
  valueCols: [],
  pivotCols: [],
  pivotMode: false,
  groupKeys: [],
  filterModel: {},
  sortModel: [],
});

/**
 * The latest rows of the user's stream (POST /user/alert_notifications, first
 * page). `staleTime: 0` - the socket nudge and the read mutations invalidate
 * it, and the dropdown mounts fresh on every open.
 */
export function injectLatestAlertNotificationsQuery(
  scope: Signal<AlertNotificationsScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? latestAlertNotificationsQueryKey(current)
        : ([...ALERT_NOTIFICATIONS_ROOT_KEY, 'latest', null] as const),
      queryFn: () =>
        firstValueFrom(
          userApi.getAlertNotificationsAgGrid(latestRowsRequest()),
        ).then((page) => (page.rowData ?? []) as AlertNotification[]),
      enabled: !!current,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * The grid endpoint's request for the stream: the first rows newest trigger
 * first, within the rail's day range when one is picked (an ag-grid date
 * `inRange` filter on `time_triggered`, the bounds as whole days the backend
 * reads in the user's timezone).
 */
const streamRowsRequest = (
  endRow: number,
  range: AlertNotificationDayRange | null,
): IServerSideGetRowsRequest => ({
  ...latestRowsRequest(endRow),
  filterModel: range
    ? {
        time_triggered: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: `${range.from} 00:00:00`,
          dateTo: `${range.to} 23:59:59`,
        },
      }
    : {},
});

/**
 * The stream view (cards mode of the page): the first `pages` steps of the
 * user's rows, newest trigger first, through the grid endpoint like the object
 * card lists, within the day range the rail picked. `keepPreviousData` keeps
 * the loaded cards on screen while the next step or an invalidation refetches;
 * the read mutations and the socket nudge invalidate it through the root key.
 */
export function injectAlertNotificationStreamQuery(
  scope: Signal<AlertNotificationsScope | null>,
  pages: Signal<number>,
  range: Signal<AlertNotificationDayRange | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();
    const endRow = pages() * ALERT_NOTIFICATION_STREAM_PAGE_SIZE;
    const days = range();

    return {
      queryKey: current
        ? ([
            ...ALERT_NOTIFICATIONS_ROOT_KEY,
            'stream',
            current,
            days,
            endRow,
          ] as const)
        : ([
            ...ALERT_NOTIFICATIONS_ROOT_KEY,
            'stream',
            null,
            days,
            endRow,
          ] as const),
      queryFn: () =>
        firstValueFrom(
          userApi.getAlertNotificationsAgGrid(streamRowsRequest(endRow, days)),
        ).then((page) => ({
          rowCount: page.rowCount ?? 0,
          rows: (page.rowData ?? []) as AlertNotification[],
        })),
      enabled: !!current,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * The day rail of the stream (GET /user/alert_notification_days): rows and
 * unread rows per day of the retention window in the user's timezone. Under
 * the stream root key, so the read mutations and the socket nudge refresh it
 * with the cards.
 */
export function injectAlertNotificationDaysQuery(
  scope: Signal<AlertNotificationsScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? alertNotificationDaysQueryKey(current)
        : ([...ALERT_NOTIFICATIONS_ROOT_KEY, 'days', null] as const),
      queryFn: () =>
        firstValueFrom(userApi.getAlertNotificationDays()).then(
          (result): AlertNotificationDays => ({
            timezone: result?.timezone ?? '',
            days: result?.days ?? [],
          }),
        ),
      enabled: !!current,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * The header badge (GET /user/alert_notification_count). The socket nudge
 * invalidates it when a trigger fans out to the user; the 60 s poll is the
 * fallback for a dropped socket, the same cadence the mobile app uses, and
 * the count is one indexed query on the backend.
 */
export function injectAlertNotificationCountQuery(
  scope: Signal<AlertNotificationsScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? alertNotificationCountQueryKey(current)
        : ([...ALERT_NOTIFICATION_COUNT_ROOT_KEY, null] as const),
      queryFn: () =>
        firstValueFrom(userApi.getAlertNotificationCount()).then(
          (result) => result?.count ?? 0,
        ),
      enabled: !!current,
      refetchInterval: 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/** One row's detail (GET /alert_notification/{eventId}): snapshot, posts, deliveries. */
export function injectAlertNotificationDetailQuery(
  eventId: Signal<number | null>,
) {
  const api = inject(AlertNotificationApiService);

  return injectQuery(() => {
    const current = eventId();

    return {
      queryKey: current
        ? alertNotificationDetailQueryKey(current)
        : ([...ALERT_NOTIFICATION_ROOT_KEY, null] as const),
      queryFn: () => firstValueFrom(api.getAlertNotification(current!)),
      enabled: !!current,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * Drops every cached stream entry of the user: the latest rows and the unread
 * count. The socket's `alert_event` nudge and the read mutations call it; the
 * grid page is SSRM and refreshes through its own wrapper.
 */
export function invalidateAlertNotifications(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ALERT_NOTIFICATIONS_ROOT_KEY });
  queryClient.invalidateQueries({
    queryKey: ALERT_NOTIFICATION_COUNT_ROOT_KEY,
  });
}

/**
 * Marks one row read (PATCH /alert_notification/{eventId}/read) - the explicit
 * open of the event, never the list or the badge (D-1). The response row
 * patches the detail entry in place; the list and the count are invalidated.
 * No toast: reading is not an action the user asked for feedback on.
 */
export function injectMarkAlertNotificationReadMutation() {
  const api = inject(AlertNotificationApiService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationKey: [...ALERT_NOTIFICATION_ROOT_KEY, 'read'] as const,
    mutationFn: (eventId: number) =>
      firstValueFrom(api.markAlertNotificationRead(eventId)),
    onSuccess: (row) => {
      const key = alertNotificationDetailQueryKey(row.id);
      const updatedAt = queryClient.getQueryState(key)?.dataUpdatedAt;
      queryClient.setQueryData<AlertNotificationDetail>(
        key,
        (current) => (current ? { ...current, event: row } : current),
        updatedAt ? { updatedAt } : undefined,
      );
      invalidateAlertNotifications(queryClient);
    },
  }));
}

/** Marks every unread row of the active company read (PATCH /user/alert_notifications_read_all). */
export function injectMarkAllAlertNotificationsReadMutation() {
  const userApi = inject(UserApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: () => firstValueFrom(userApi.markAllAlertNotificationsRead()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALERT_NOTIFICATION_ROOT_KEY });
      invalidateAlertNotifications(queryClient);
    },
    onError: () =>
      toast.showError(
        translate.instant('alert-notifications.toast.mark-all-read-failed'),
      ),
  }));
}
